import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Save, X, BarChart3, Layers } from 'lucide-react';
import { genId, emptyRow, calcRow, getRoomTotal, defaultRoomNames } from '@/components/boq/boqCalc';
import RoomSpreadsheet, { GrandTotalView } from '@/components/boq/RoomSpreadsheet';
import ItemCostSheetPicker from '@/components/boq/ItemCostSheetPicker';

// Map a stored template item -> in-memory BOQ-shaped row
function toBoqRow(it, sno, rateMap) {
  const variantId = it.variant_id || it.dnb_variant_id || '';
  const live = rateMap[variantId];
  const base = {
    id: it.id || genId(),
    sno,
    description: it.item_name || it.description || '',
    specification: it.specification || '',
    dnb_item_id: it.item_id || it.dnb_item_id || '',
    dnb_variant_id: variantId,
    l: Number(it.l) || 0,
    w: Number(it.w) || 0,
    h: Number(it.h) || 0,
    nos: it.nos != null && it.nos !== '' ? Number(it.nos) : (Number(it.default_quantity) || 1),
    unit: (live?.uom) || it.unit || 'Sq.ft',
    area_basis: it.area_basis || 'auto',
    rate: live ? live.rate : 0, // live rate, never persisted
  };
  return calcRow(base);
}

export default function TemplateBOQEditor({ open, onOpenChange, template, readOnly, onSaved }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [rooms, setRooms] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [rateMap, setRateMap] = useState({});
  const [rateMapReady, setRateMapReady] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerRowIdx, setPickerRowIdx] = useState(null);
  const [editingRoomName, setEditingRoomName] = useState(null);
  const [showAddRoom, setShowAddRoom] = useState(false);

  // Build live rate map from Item & Cost Sheet
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get('/db-items');
        const map = {};
        (res.data || []).forEach(item => (item.variants || []).forEach(v => { map[v.id] = { rate: v.rate, uom: v.uom, item_id: item.id }; }));
        setRateMap(map);
      } catch { setRateMap({}); }
      finally { setRateMapReady(true); }
    })();
  }, [open]);

  // Hydrate template into editor once rate map ready
  useEffect(() => {
    if (!template || !rateMapReady) return;
    setName(template.name || '');
    setDescription(template.description || '');
    setTags((template.tags || []).join(', '));
    const hydrated = (template.rooms || []).map(r => ({
      id: r.id || genId(),
      name: r.name || 'Room',
      items: (r.items || []).map((it, i) => toBoqRow(it, i + 1, rateMap)),
    }));
    setRooms(hydrated.length > 0 ? hydrated : []);
    setActiveTab(0);
    setDirty(false);
  }, [template, rateMapReady, rateMap]);

  const addRoom = (roomName) => {
    setRooms(prev => [...prev, { id: genId(), name: roomName, items: [emptyRow(1), emptyRow(2), emptyRow(3)].map(r => ({ ...r, rate: 0 })) }]);
    setActiveTab(rooms.length);
    setDirty(true);
    setShowAddRoom(false);
  };
  const removeRoom = (idx) => {
    setRooms(prev => prev.filter((_, i) => i !== idx));
    setActiveTab(prev => Math.max(0, typeof prev === 'number' ? Math.min(prev, rooms.length - 2) : 0));
    setDirty(true);
  };
  const renameRoom = (idx, val) => { setRooms(prev => prev.map((r, i) => i === idx ? { ...r, name: val } : r)); setDirty(true); };

  const updateCell = (roomIdx, rowIdx, key, value) => {
    setRooms(prev => {
      const updated = [...prev];
      const items = [...updated[roomIdx].items];
      items[rowIdx] = calcRow({ ...items[rowIdx], [key]: value });
      updated[roomIdx] = { ...updated[roomIdx], items };
      return updated;
    });
    setDirty(true);
  };
  const addRow = (roomIdx) => {
    setRooms(prev => {
      const updated = [...prev];
      const items = [...updated[roomIdx].items];
      items.push({ ...emptyRow(items.length + 1), rate: 0 });
      updated[roomIdx] = { ...updated[roomIdx], items };
      return updated;
    });
    setDirty(true);
  };
  const deleteRow = (roomIdx, rowIdx) => {
    setRooms(prev => {
      const updated = [...prev];
      let items = updated[roomIdx].items.filter((_, i) => i !== rowIdx);
      items = items.map((item, i) => ({ ...item, sno: i + 1 }));
      updated[roomIdx] = { ...updated[roomIdx], items };
      return updated;
    });
    setDirty(true);
  };

  // Pick item+variant -> fills description/spec/unit + LIVE rate (rate not persisted)
  const applyDbSelection = (item, variant) => {
    if (pickerRowIdx === null || typeof activeTab !== 'number') return;
    setRooms(prev => {
      const updated = [...prev];
      const items = [...updated[activeTab].items];
      const row = { ...items[pickerRowIdx] };
      row.description = item.item_name;
      row.specification = variant.specification;
      row.dnb_item_id = item.id;
      row.dnb_variant_id = variant.id;
      row.unit = variant.uom;
      row.rate = variant.rate;
      items[pickerRowIdx] = calcRow(row);
      updated[activeTab] = { ...updated[activeTab], items };
      return updated;
    });
    setDirty(true);
    setShowPicker(false);
    toast.success(`Added: ${item.item_name} — ${variant.specification}`);
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Template name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        tags: tags.split(',').map(t => t.trim()).filter(Boolean),
        rooms: rooms.map((r, ri) => ({
          id: r.id, name: r.name, sort_order: ri,
          items: r.items.map((it, ii) => ({
            id: it.id,
            item_id: it.dnb_item_id || '',
            variant_id: it.dnb_variant_id || '',
            item_name: it.description || '',
            specification: it.specification || '',
            unit: it.unit || 'Sq.ft',
            l: Number(it.l) || 0, w: Number(it.w) || 0, h: Number(it.h) || 0,
            nos: Number(it.nos) || 1,
            qty: Number(it.qty) || 0,
            area_basis: it.area_basis || 'auto',
            default_quantity: Number(it.qty) || 0,
            sort_order: ii,
            // rate intentionally NOT stored — templates are rate-free
          })),
        })),
      };
      await api.put(`/templates/${template.id}`, payload);
      toast.success('Template saved');
      setDirty(false);
      onSaved();
      onOpenChange(false);
    } catch { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const grandTotal = rooms.reduce((s, r) => s + getRoomTotal(r), 0);
  const currentRoom = typeof activeTab === 'number' ? rooms[activeTab] : null;
  const currentRoomTotal = currentRoom ? getRoomTotal(currentRoom) : 0;
  const availableRoomNames = defaultRoomNames.filter(n => !rooms.some(r => r.name === n));
  const totalItems = rooms.reduce((s, r) => s + r.items.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2 text-base">
            <Layers size={16} className="text-[#3A8C8C]" />
            {readOnly ? 'View Template' : 'Edit Template'}
            {dirty && <span className="text-[10px] text-[#C67D5B] bg-[#C67D5B]/10 px-2 py-0.5 rounded-full">Unsaved</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 mt-2 pr-1" data-testid="template-boq-editor">
          {/* Template meta */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Template Name *</Label>
              <Input value={name} onChange={e => { setName(e.target.value); setDirty(true); }} disabled={readOnly}
                className="mt-1 h-9 rounded-lg border-[#D4D0C8]" data-testid="template-edit-name" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] uppercase">Tags (comma separated)</Label>
              <Input value={tags} onChange={e => { setTags(e.target.value); setDirty(true); }} disabled={readOnly}
                placeholder="e.g. premium, compact" className="mt-1 h-9 rounded-lg border-[#D4D0C8]" data-testid="template-edit-tags" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#6B7280] uppercase">Description</Label>
            <textarea value={description} onChange={e => { setDescription(e.target.value); setDirty(true); }} disabled={readOnly}
              className="mt-1 w-full h-12 px-3 py-2 text-sm border border-[#D4D0C8] rounded-lg resize-none" data-testid="template-edit-description" />
          </div>

          <div className="flex items-center gap-2 px-3 py-2 bg-[#3A8C8C]/5 border border-[#3A8C8C]/15 rounded-lg">
            <p className="text-[11px] text-[#3A8C8C]">This is the exact BOQ/Estimate spreadsheet. Rates shown are pulled <strong>live</strong> from the Item &amp; Cost Sheet — templates store no rates. {rooms.length} rooms · {totalItems} items.</p>
          </div>

          {/* Room tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="template-room-tabs">
            {rooms.map((room, idx) => (
              <button key={room.id} onClick={() => setActiveTab(idx)} onDoubleClick={() => !readOnly && setEditingRoomName(idx)}
                className={`group relative px-3 py-1.5 rounded-t-lg text-xs font-medium transition-all whitespace-nowrap border-b-2
                  ${activeTab === idx ? 'bg-white text-[#3A8C8C] border-[#3A8C8C] shadow-sm' : 'bg-[#F5F3EF] text-[#6B7280] border-transparent hover:bg-[#E8E6E1]'}`}
                data-testid={`template-room-tab-${idx}`}>
                {editingRoomName === idx ? (
                  <input autoFocus value={room.name} onChange={e => renameRoom(idx, e.target.value)}
                    onBlur={() => setEditingRoomName(null)} onKeyDown={e => e.key === 'Enter' && setEditingRoomName(null)}
                    className="w-24 bg-transparent outline-none text-xs" onClick={e => e.stopPropagation()} />
                ) : (<span>{room.name}</span>)}
                {!readOnly && rooms.length > 0 && activeTab === idx && (
                  <button onClick={e => { e.stopPropagation(); removeRoom(idx); }}
                    className="ml-1.5 opacity-0 group-hover:opacity-100 text-[#C4727F] transition-opacity" data-testid={`template-remove-room-${idx}`}>
                    <X size={11} strokeWidth={2} />
                  </button>
                )}
              </button>
            ))}
            {rooms.length > 0 && (
              <button onClick={() => setActiveTab('grand-total')}
                className={`px-3 py-1.5 rounded-t-lg text-xs font-semibold transition-all whitespace-nowrap border-b-2 flex items-center gap-1.5
                  ${activeTab === 'grand-total' ? 'bg-white text-[#3A8C8C] border-[#3A8C8C] shadow-sm' : 'bg-[#F5F3EF] text-[#6B7280] border-transparent hover:bg-[#E8E6E1]'}`}
                data-testid="template-grand-total-tab">
                <BarChart3 size={12} strokeWidth={1.5} /> Grand Total
              </button>
            )}
            {!readOnly && (
              <button onClick={() => setShowAddRoom(true)} className="p-1.5 rounded-lg hover:bg-[#E8E6E1] transition-colors" data-testid="template-add-room-btn">
                <Plus size={14} strokeWidth={1.5} className="text-[#3A8C8C]" />
              </button>
            )}
          </div>

          {activeTab === 'grand-total' && <GrandTotalView rooms={rooms} grandTotal={grandTotal} />}

          {typeof activeTab === 'number' && currentRoom && (
            <RoomSpreadsheet
              room={currentRoom} roomIdx={activeTab} roomTotal={currentRoomTotal} grandTotal={grandTotal}
              onUpdateCell={updateCell} onAddRow={addRow} onDeleteRow={deleteRow}
              onOpenPicker={(rowIdx) => { setPickerRowIdx(rowIdx); setShowPicker(true); }}
              rateReadOnly readOnly={readOnly}
            />
          )}

          {rooms.length === 0 && (
            <div className="text-center py-10 border border-dashed border-[#D4D0C8] rounded-xl">
              <Layers size={28} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
              <p className="text-sm text-[#6B7280]">No rooms yet{readOnly ? '' : ' — add a room to start building this template'}</p>
            </div>
          )}
        </div>

        {!readOnly && (
          <div className="flex gap-2 pt-3 border-t border-[#E8E6E1] mt-2">
            <button onClick={() => onOpenChange(false)} className="flex-1 h-10 rounded-full border border-[#D4D0C8] text-sm font-medium text-[#6B7280] hover:bg-[#E8E6E1]">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="template-save-btn"
              className="flex-1 h-10 bg-[#3A8C8C] text-white rounded-full font-medium text-sm hover:bg-[#2E7A7A] disabled:opacity-50 flex items-center justify-center gap-1.5">
              <Save size={14} /> {saving ? 'Saving…' : 'Save Template'}
            </button>
          </div>
        )}

        {/* Add Room dialog */}
        <Dialog open={showAddRoom} onOpenChange={setShowAddRoom}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-['Manrope'] text-base">Add Room</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              {availableRoomNames.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Quick Add</p>
                  <div className="flex flex-wrap gap-1.5">
                    {availableRoomNames.slice(0, 12).map(n => (
                      <button key={n} onClick={() => addRoom(n)}
                        className="px-2.5 py-1 rounded-md text-xs bg-[#F5F3EF] text-[#2C2C2E] hover:bg-[#3A8C8C]/10 hover:text-[#3A8C8C] transition-colors">{n}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-[10px] text-[#6B7280] uppercase tracking-wider font-semibold">Custom Name</p>
                <div className="flex gap-2">
                  <Input id="tmpl-custom-room" placeholder="e.g. Pooja Room" className="h-9 text-sm rounded-lg border-[#D4D0C8]"
                    onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) addRoom(e.target.value.trim()); }} />
                  <button onClick={() => { const el = document.getElementById('tmpl-custom-room'); if (el?.value.trim()) addRoom(el.value.trim()); }}
                    className="px-3 py-1.5 rounded-lg bg-[#3A8C8C] text-white text-xs font-medium hover:bg-[#2E7A7A]">Add</button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <ItemCostSheetPicker open={showPicker} onOpenChange={setShowPicker} onSelect={applyDbSelection} />
      </DialogContent>
    </Dialog>
  );
}
